<template>
  <div class="register-container">
    <canvas id="canvas-bg"></canvas>
    <div class="split-container">
      <div class="left-intro-area">
        <h1 class="hero-title">
          加入<br>
          <span class="highlight">小葱聊天室</span>
        </h1>
        <p class="hero-desc">
          创建您的专属账号，开启安全、快速、沉浸式的实时沟通体验。让我们一起构建一个酷酷的对话空间。
        </p>
      </div>
      <div class="right-login-area">
        <div class="register-card">
          <div class="card-header">
            <h2>小葱聊天室</h2>
            <p>创建新账号</p>
          </div>
          
          <div class="form">
            <!-- 昵称单独一行 -->
            <div class="form-group">
              <input v-model="form.nickname" class="custom-input" placeholder="昵称" />
            </div>
            
            <!-- 其他信息两列布局 -->
            <div class="form-row">
              <!-- 左列 -->
              <div class="form-col">
                <div class="form-group">
                  <input v-model="form.email" @blur="checkEmail" class="custom-input" :class="{ 'error-input': emailError }" placeholder="邮箱" />
                  <div v-if="emailError" class="error-message">{{ emailError }}</div>
                </div>
                <div class="form-group code-group">
                  <input v-model="form.emailCode" class="custom-input code-input" placeholder="邮箱验证码" />
                  <button @click="sendEmailCode" :disabled="emailCountdown > 0" class="btn-code">
                    {{ emailCountdown > 0 ? `${emailCountdown}秒` : '发送验证码' }}
                  </button>
                </div>
                <div class="form-group">
                  <input 
                    v-model="form.password" 
                    type="password" 
                    class="custom-input"
                    :class="{ 'error-input': passwordError && form.password.length < 8 }"
                    placeholder="密码（至少8位）" 
                    @input="handlePasswordInput"
                    @blur="validatePassword"
                  />
                  <div v-if="passwordError && form.password.length < 8" class="error-message">{{ passwordError }}</div>
                  <!-- 密码强度指示器 -->
                  <div v-if="form.password && passwordStrengthLevel > 0" class="password-strength">
                    <div class="strength-label">密码强度：</div>
                    <div class="strength-bars">
                      <div class="strength-bar" :class="'strength-' + passwordStrengthLevel"></div>
                    </div>
                    <div class="strength-text">{{ getStrengthText(passwordStrengthLevel) }}</div>
                  </div>
                </div>
              </div>
              
              <!-- 右列 -->
              <div class="form-col">
                <div class="form-group">
                  <input v-model="form.phone" @blur="checkPhone" class="custom-input" :class="{ 'error-input': phoneError }" placeholder="手机号" />
                  <div v-if="phoneError" class="error-message">{{ phoneError }}</div>
                </div>
                <div class="form-group code-group">
                  <input v-model="form.phoneCode" class="custom-input code-input" placeholder="手机验证码" />
                  <button @click="sendSmsCode" :disabled="phoneCountdown > 0" class="btn-code">
                    {{ phoneCountdown > 0 ? `${phoneCountdown}秒` : '发送验证码' }}
                  </button>
                </div>
                <div class="form-group">
                  <input 
                    v-model="form.confirmPassword" 
                    type="password" 
                    class="custom-input" 
                    :class="{ 'error-input': passwordError && form.password.length >= 8 }" 
                    placeholder="确认密码" 
                    @input="handleConfirmPasswordInput"
                    @blur="validateConfirmPassword"
                  />
                  <div v-if="passwordError && form.password.length >= 8" class="error-message">{{ passwordError }}</div>
                </div>
              </div>
            </div>
            
            <!-- 隐私条例同意 -->
            <div class="privacy-agreement" :class="{ 'shake': privacyShake }">
              <label class="privacy-checkbox">
                <input 
                  type="checkbox" 
                  v-model="agreedToPrivacy" 
                  class="checkbox-input"
                />
                <span class="checkbox-label">
                  小葱已阅读并同意
                  <span class="privacy-link" @click.stop="showPrivacyModal = true">《隐私政策与服务条款》</span>
                </span>
              </label>
              <div v-if="privacyError" class="error-message">{{ privacyError }}</div>
            </div>
            
            <!-- 注册按钮横跨两列 -->
            <button @click="handleRegister" class="login-btn">注 册</button>
          </div>
          
          <div class="card-footer">
            <span @click="$router.push('/login')" class="footer-link">
              <span class="link-highlight">已有账号？去登录</span>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 隐私政策弹窗 -->
    <transition name="modal-fade">
      <div v-if="showPrivacyModal" class="modal-overlay" @click.self="showPrivacyModal = false">
        <div class="modal-content privacy-modal">
          <div class="modal-header">
            <h3>隐私政策与服务条款</h3>
            <button class="close-btn" @click="showPrivacyModal = false">×</button>
          </div>
          <div class="modal-body privacy-content">
            <div class="privacy-section">
              <h4>一、隐私保护</h4>
              <p>1. 小葱承诺保护您的个人信息安全，严格遵守相关法律法规，不会未经您同意向第三方泄露、出售或非法使用您的个人信息。</p>
              <p>2. 小葱收集的信息包括但不限于：昵称、邮箱、手机号等注册信息，以及您在使用服务过程中产生的聊天记录、文件等数据。</p>
              <p>3. 小葱采用行业标准的安全技术措施保护您的数据安全，包括数据加密、访问控制等。</p>
              <p>4. 您有权随时查看、修改或删除您的个人信息，也可通过联系小葱申请注销账号。</p>
            </div>
            
            <div class="privacy-section">
              <h4>二、服务使用规范</h4>
              <p>1. 您在使用本服务时，应遵守国家法律法规，不得利用本服务从事违法违规活动。</p>
              <p>2. 禁止发布、传播违法、有害、侵权、虚假、骚扰、诽谤、威胁、色情、暴力等不良信息。</p>
              <p>3. 禁止利用本服务进行商业推广、广告宣传等未经授权的商业活动。</p>
              <p>4. 禁止恶意注册、批量注册账号，或使用技术手段干扰服务正常运行。</p>
              <p>5. 如违反上述规定，小葱有权暂停或终止您的账号使用，并保留追究法律责任的权利。</p>
            </div>
            
            <div class="privacy-section">
              <h4>三、账号安全</h4>
              <p>1. 您有责任妥善保管账号密码，不得将账号密码告知他人或与他人共享账号。</p>
              <p>2. 如发现账号被盗用或存在安全风险，请立即联系小葱处理。</p>
              <p>3. 因您个人原因导致账号被盗用或信息泄露的，小葱不承担责任。</p>
            </div>
            
            <div class="privacy-section">
              <h4>四、服务变更与终止</h4>
              <p>1. 小葱有权根据业务发展需要调整、变更或终止部分或全部服务功能。</p>
              <p>2. 如因不可抗力导致服务中断或终止，小葱不承担责任，但会尽力减少对您的影响。</p>
              <p>3. 您有权随时停止使用本服务并注销账号。</p>
            </div>
            
            <div class="privacy-section">
              <h4>五、免责声明</h4>
              <p>1. 本服务按"现状"提供，小葱不对服务的及时性、安全性、准确性作任何保证。</p>
              <p>2. 因使用或无法使用本服务而产生的任何直接或间接损失，小葱不承担责任。</p>
              <p>3. 用户之间通过本服务产生的纠纷，由用户自行解决，小葱不承担任何责任。</p>
            </div>
            
            <div class="privacy-section">
              <h4>六、其他条款</h4>
              <p>1. 本条款的最终解释权归小葱所有。</p>
              <p>2. 小葱有权随时修改本条款，修改后的条款将在平台上公布，继续使用服务即视为接受修改后的条款。</p>
              <p>3. 如本条款的任何条款被认定为无效或不可执行，不影响其他条款的效力。</p>
            </div>
            
            <div class="privacy-footer">
              <p class="update-date">最后更新时间：2026年1月31日</p>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-primary" @click="agreeAndClosePrivacy">小葱已阅读并同意</button>
            <button class="btn-secondary" @click="showPrivacyModal = false">关闭</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { register, sendEmailCode, sendSmsCode, checkEmail, checkPhone } from '@/api/auth'

export default {
  name: 'Register',
  data() {
    return {
      form: {
        nickname: '',
        email: '',
        emailCode: '',
        phone: '',
        phoneCode: '',
        password: '',
        confirmPassword: ''
      },
      emailError: '',
      phoneError: '',
      passwordError: '',
      emailCountdown: 0,
      phoneCountdown: 0,
      passwordStrengthLevel: 0,
      agreedToPrivacy: false,
      privacyError: '',
      showPrivacyModal: false,
      privacyShake: false
    }
  },
  mounted() {
    // 使用 nextTick 确保 DOM 完全渲染后再初始化 Canvas
    this.$nextTick(() => {
      // 延迟一点时间确保路由过渡动画完成
      setTimeout(() => {
        this.initCanvas()
      }, 100)
    })
  },
  methods: {
    validateEmail(email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return re.test(email)
    },
    validatePhone(phone) {
      const re = /^1[3-9]\d{9}$/
      return re.test(phone)
    },
    async checkEmail() {
      if (!this.form.email) {
        this.emailError = ''
        return
      }
      if (!this.validateEmail(this.form.email)) {
        this.emailError = '邮箱格式不正确'
        return
      }
      try {
        const res = await checkEmail(this.form.email)
        if (res.exists) {
          this.emailError = '该邮箱已被注册'
        } else {
          this.emailError = ''
        }
      } catch (e) {
        this.emailError = ''
      }
    },
    async checkPhone() {
      if (!this.form.phone) {
        this.phoneError = ''
        return
      }
      if (!this.validatePhone(this.form.phone)) {
        this.phoneError = '手机号格式不正确'
        return
      }
      try {
        const res = await checkPhone(this.form.phone)
        if (res.exists) {
          this.phoneError = '该手机号已被注册'
        } else {
          this.phoneError = ''
        }
      } catch (e) {
        this.phoneError = ''
      }
    },
    checkStrength(pwd) {
      if (!pwd) return 0
      let score = 0
      if (pwd.length >= 8) score++
      if (/[A-Z]/.test(pwd)) score++
      if (/[a-z]/.test(pwd)) score++
      if (/\d/.test(pwd)) score++
      if (/[^A-Za-z0-9]/.test(pwd)) score++
      if (score >= 4) return 3
      if (score === 3) return 2
      return score > 0 ? 1 : 0
    },
    getStrengthText(level) {
      if (level === 3) return '强'
      if (level === 2) return '中'
      if (level === 1) return '弱'
      return ''
    },
    handleConfirmPasswordInput() {
      if (this.form.password && this.form.confirmPassword) {
        if (this.form.password !== this.form.confirmPassword) {
          this.passwordError = '两次输入的密码不一致'
        } else {
          this.passwordError = ''
        }
      }
    },
    validateConfirmPassword() {
      if (!this.form.confirmPassword) {
        if (this.form.password) {
          this.passwordError = '请再次输入密码'
        }
        return false
      }
      if (this.form.password !== this.form.confirmPassword) {
        this.passwordError = '两次输入的密码不一致'
        return false
      }
      this.passwordError = ''
      return true
    },
    handlePasswordInput() {
      const pwd = this.form.password || ''
      if (!pwd) {
        this.passwordStrengthLevel = 0
        this.passwordError = ''
        return
      }
      this.passwordStrengthLevel = this.checkStrength(pwd)
      if (pwd.length < 8) {
        this.passwordError = '密码长度至少8位'
      } else if (this.form.confirmPassword && pwd !== this.form.confirmPassword) {
        this.passwordError = '两次输入的密码不一致'
      } else {
        this.passwordError = ''
      }
    },
    validatePassword() {
      if (this.form.password.length < 8) {
        this.passwordError = '密码长度至少8位'
        this.passwordStrengthLevel = this.checkStrength(this.form.password)
        return false
      }
      if (this.form.password !== this.form.confirmPassword) {
        this.passwordError = '两次输入的密码不一致'
        this.passwordStrengthLevel = this.checkStrength(this.form.password)
        return false
      }
      this.passwordError = ''
      this.passwordStrengthLevel = this.checkStrength(this.form.password)
      return true
    },
    async sendEmailCode() {
      if (!this.form.email) {
        this.$message.warning('请先输入邮箱')
        return
      }
      if (!this.validateEmail(this.form.email)) {
        this.emailError = '邮箱格式不正确'
        this.$message.warning('邮箱格式不正确')
        return
      }
      try {
        const res = await checkEmail(this.form.email)
        if (res.exists) {
          this.emailError = '该邮箱已被注册'
          this.$message.warning('该邮箱已被注册')
          return
        }
        this.emailError = ''
      } catch (e) {
        // 检查失败不影响发送验证码
      }
      try {
        await sendEmailCode({ email: this.form.email, scene: 'register' })
        this.startCountdown('email')
        this.$message.success('验证码已发送')
      } catch (e) {
        this.$message.error(e.message || '发送失败')
      }
    },
    async sendSmsCode() {
      if (!this.form.phone) {
        this.$message.warning('请先输入手机号')
        return
      }
      if (!this.validatePhone(this.form.phone)) {
        this.phoneError = '手机号格式不正确'
        this.$message.warning('手机号格式不正确')
        return
      }
      try {
        const res = await checkPhone(this.form.phone)
        if (res.exists) {
          this.phoneError = '该手机号已被注册'
          this.$message.warning('该手机号已被注册')
          return
        }
        this.phoneError = ''
      } catch (e) {
        // 检查失败不影响发送验证码
      }
      try {
        await sendSmsCode({ phone: this.form.phone, scene: 'register' })
        this.startCountdown('phone')
        this.$message.success('验证码已发送')
      } catch (e) {
        this.$message.error(e.message || '发送失败')
      }
    },
    agreeAndClosePrivacy() {
      this.agreedToPrivacy = true
      this.showPrivacyModal = false
      this.privacyError = ''
      this.privacyShake = false
    },
    triggerPrivacyShake() {
      this.privacyShake = true
      setTimeout(() => {
        this.privacyShake = false
      }, 500)
    },
    async handleRegister() {
      // 先检查信息是否填写完整
      if (!this.form.nickname || !this.form.email || !this.form.phone || !this.form.password || !this.form.confirmPassword) {
        this.$message.warning('请填写完整信息')
        return
      }
      if (!this.validatePassword()) {
        return
      }
      if (this.emailError || this.phoneError) {
        this.$message.warning('请先解决邮箱或手机号问题')
        return
      }
      
      // 信息填写完整后，检查是否同意隐私条例
      if (!this.agreedToPrivacy) {
        this.privacyError = '请勾选隐私协议'
        this.$message.warning('请勾选隐私协议')
        // 触发震动效果
        this.triggerPrivacyShake()
        return
      }
      this.privacyError = ''
      try {
        await register(this.form)
        this.$message.success('注册成功，请登录')
        this.$router.push('/login')
      } catch (e) {
        this.$message.error(e.message || '注册失败')
      }
    },
    startCountdown(type) {
      if (type === 'email') {
        this.emailCountdown = 60
        const timer = setInterval(() => {
          this.emailCountdown--
          if (this.emailCountdown <= 0) {
            clearInterval(timer)
          }
        }, 1000)
      } else if (type === 'phone') {
        this.phoneCountdown = 60
        const timer = setInterval(() => {
          this.phoneCountdown--
          if (this.phoneCountdown <= 0) {
            clearInterval(timer)
          }
        }, 1000)
      }
    },
    initCanvas() {
      // 如果已经初始化过，先清理
      if (this._canvasCleanup) {
        this._canvasCleanup()
        this._canvasCleanup = null
        this._canvasAnimationId = null
      }
      
      const canvas = document.getElementById('canvas-bg')
      if (!canvas) {
        // 如果 Canvas 元素不存在，延迟重试（最多重试5次）
        if (!this._canvasRetryCount) {
          this._canvasRetryCount = 0
        }
        if (this._canvasRetryCount < 5) {
          this._canvasRetryCount++
          console.warn(`Canvas 元素未找到，100ms 后重试 (${this._canvasRetryCount}/5)...`)
          setTimeout(() => {
            this.initCanvas()
          }, 100)
        } else {
          console.error('Canvas 元素初始化失败，已达到最大重试次数')
          this._canvasRetryCount = 0
        }
        return
      }
      
      // 重置重试计数
      this._canvasRetryCount = 0
      
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        console.error('无法获取 Canvas 2D 上下文')
        return
      }
      
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight

      let particlesArray
      const numberOfParticles = 70

      const mouse = { x: null, y: null, radius: 150 }
      const handleMouseMove = (event) => {
        mouse.x = event.x
        mouse.y = event.y
      }
      window.addEventListener('mousemove', handleMouseMove)

      class Particle {
        constructor(x, y, directionX, directionY, size, color) {
          this.x = x
          this.y = y
          this.directionX = directionX
          this.directionY = directionY
          this.size = size
          this.color = color
        }
        draw() {
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2, false)
          ctx.fillStyle = 'rgba(100, 255, 218, 0.4)'
          ctx.fill()
        }
        update() {
          if (this.x > canvas.width || this.x < 0) {
            this.directionX = -this.directionX
          }
          if (this.y > canvas.height || this.y < 0) {
            this.directionY = -this.directionY
          }
          
          let dx = mouse.x - this.x
          let dy = mouse.y - this.y
          let distance = Math.sqrt(dx * dx + dy * dy)
          if (distance < mouse.radius + this.size) {
            if (mouse.x < this.x && this.x < canvas.width - this.size * 10) {
              this.x += 1
            }
            if (mouse.x > this.x && this.x > this.size * 10) {
              this.x -= 1
            }
            if (mouse.y < this.y && this.y < canvas.height - this.size * 10) {
              this.y += 1
            }
            if (mouse.y > this.y && this.y > this.size * 10) {
              this.y -= 1
            }
          }
          this.x += this.directionX * 0.3
          this.y += this.directionY * 0.3
          this.draw()
        }
      }

      const init = () => {
        particlesArray = []
        for (let i = 0; i < numberOfParticles; i++) {
          let size = (Math.random() * 2) + 1
          let x = (Math.random() * ((innerWidth - size * 2) - (size * 2)) + size * 2)
          let y = (Math.random() * ((innerHeight - size * 2) - (size * 2)) + size * 2)
          let directionX = (Math.random() * 2) - 1
          let directionY = (Math.random() * 2) - 1
          particlesArray.push(new Particle(x, y, directionX, directionY, size, 'white'))
        }
      }

      const connect = () => {
        for (let a = 0; a < particlesArray.length; a++) {
          for (let b = a; b < particlesArray.length; b++) {
            let distance = ((particlesArray[a].x - particlesArray[b].x) * (particlesArray[a].x - particlesArray[b].x)) + ((particlesArray[a].y - particlesArray[b].y) * (particlesArray[a].y - particlesArray[b].y))
            if (distance < (canvas.width / 7) * (canvas.height / 7)) {
              let opacityValue = 1 - (distance / 20000)
              ctx.strokeStyle = 'rgba(100, 255, 218,' + opacityValue * 0.15 + ')'
              ctx.lineWidth = 1
              ctx.beginPath()
              ctx.moveTo(particlesArray[a].x, particlesArray[a].y)
              ctx.lineTo(particlesArray[b].x, particlesArray[b].y)
              ctx.stroke()
            }
          }
        }
      }

      let animationId
      const animate = () => {
        animationId = requestAnimationFrame(animate)
        ctx.clearRect(0, 0, innerWidth, innerHeight)
        for (let i = 0; i < particlesArray.length; i++) {
          particlesArray[i].update()
        }
        connect()
      }

      const handleResize = () => {
        canvas.width = innerWidth
        canvas.height = innerHeight
        mouse.radius = ((canvas.height / 80) * (canvas.width / 80))
        init()
      }

      window.addEventListener('resize', handleResize)
      init()
      animate()

      // 保存清理函数和动画ID
      this._canvasAnimationId = animationId
      this._canvasCleanup = () => {
        window.removeEventListener('resize', handleResize)
        window.removeEventListener('mousemove', handleMouseMove)
        if (this._canvasAnimationId) {
          cancelAnimationFrame(this._canvasAnimationId)
          this._canvasAnimationId = null
        }
      }
    }
  },
  beforeUnmount() {
    if (this._canvasCleanup) {
      this._canvasCleanup()
      this._canvasCleanup = null
      this._canvasAnimationId = null
    }
  },
  activated() {
    // 如果使用了 keep-alive，组件激活时重新初始化 Canvas
    this.$nextTick(() => {
      setTimeout(() => {
        this.initCanvas()
      }, 100)
    })
  },
  deactivated() {
    // 如果使用了 keep-alive，组件停用时清理 Canvas
    if (this._canvasCleanup) {
      this._canvasCleanup()
      this._canvasCleanup = null
      this._canvasAnimationId = null
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700;900&display=swap');

:root {
  --bg-deep: #020c1b;
  --bg-tech: #0a192f;
  --card-bg: rgba(16, 32, 60, 0.75);
  --text-bright: #ffffff;
  --text-muted: #8892b0;
  --accent-cyan: #64ffda;
  --accent-blue: #007bff;
  --input-bg: rgba(2, 12, 27, 0.5);
}

.register-container {
  font-family: 'Inter', sans-serif;
  background-color: var(--bg-deep);
  height: 100vh;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  color: var(--text-bright);
  position: relative;
}

#canvas-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  background: radial-gradient(ellipse at center, var(--bg-tech) 0%, var(--bg-deep) 100%);
}

.split-container {
  display: flex;
  width: 90%;
  max-width: 1400px;
  align-items: center;
  justify-content: space-between;
  z-index: 10;
  transform: skewY(-2deg);
}

.left-intro-area {
  flex: 1;
  padding-right: 60px;
  transform: skewY(2deg);
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 900;
  line-height: 1.2;
  margin-bottom: 20px;
  text-shadow: 0 5px 20px rgba(0, 0, 0, 0.5);
  color: var(--text-bright);
}

.hero-title .highlight {
  color: var(--accent-cyan);
  position: relative;
  display: inline-block;
}

.hero-title .highlight::after {
  content: '';
  position: absolute;
  bottom: 5px;
  left: 0;
  width: 100%;
  height: 8px;
  background: rgba(100, 255, 218, 0.2);
  z-index: -1;
  transform: skewX(-10deg);
}

.hero-desc {
  font-size: 1.2rem;
  color: var(--text-muted);
  line-height: 1.6;
  max-width: 480px;
}

.right-login-area {
  flex: 1;
  display: flex;
  justify-content: flex-end;
  transform: skewY(2deg);
}

.register-card {
  background: var(--card-bg);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  padding: 40px;
  border-radius: 20px;
  width: 800px;
  max-width: 90vw;
  border: 1px solid rgba(100, 255, 218, 0.1);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(100, 255, 218, 0.1);
  transition: all 0.4s ease;
}

.register-card:hover {
  transform: translateY(-10px) scale(1.02);
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.5), 0 0 50px rgba(100, 255, 218, 0.3);
  border-color: rgba(100, 255, 218, 0.3);
}

.card-header {
  text-align: center;
  margin-bottom: 30px;
}

.card-header h2 {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--text-bright);
}

.card-header p {
  color: var(--text-muted);
  font-size: 1rem;
}

.form {
  margin-bottom: 24px;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-col {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.form-group {
  margin-bottom: 20px;
}

.custom-input {
  width: 100%;
  padding: 15px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  background: var(--input-bg);
  color: var(--text-bright);
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s;
  box-sizing: border-box;
}

.custom-input:focus {
  border-color: var(--accent-cyan);
  box-shadow: 0 0 15px rgba(100, 255, 218, 0.2);
  background: rgba(2, 12, 27, 0.8);
}

.custom-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.custom-input.error-input {
  border-color: #f44336;
}

.code-group {
  display: flex;
  gap: 12px;
}

.code-input {
  flex: 1;
}

.code-group .btn-code {
  flex-shrink: 0;
  white-space: nowrap;
  padding: 12px 16px;
  font-size: 13px;
}

.btn-code {
  padding: 12px 20px;
  background: rgba(100, 255, 218, 0.1);
  border: 2px solid var(--accent-cyan);
  border-radius: 8px;
  color: var(--accent-cyan);
  cursor: pointer;
  white-space: nowrap;
  font-size: 14px;
  transition: all 0.3s;
}

.btn-code:hover:not(:disabled) {
  background: var(--accent-cyan);
  color: var(--bg-deep);
}

.btn-code:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.login-btn {
  width: 100%;
  padding: 15px;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--bg-deep);
  background: linear-gradient(135deg, var(--accent-cyan), var(--accent-blue));
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
  animation: btnPulse 3s infinite ease-in-out;
}

.login-btn:hover {
  transform: scale(1.02);
  box-shadow: 0 0 30px rgba(100, 255, 218, 0.8);
  animation-play-state: paused;
}

@keyframes btnPulse {
  0%, 100% {
    box-shadow: 0 5px 15px rgba(100, 255, 218, 0.3);
  }
  50% {
    box-shadow: 0 5px 25px rgba(100, 255, 218, 0.6);
  }
}

.card-footer {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  font-size: 0.9rem;
}

.footer-link {
  color: var(--text-muted);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.3s;
}

.footer-link:hover {
  color: var(--accent-cyan);
}

.link-highlight {
  color: var(--accent-cyan);
}

.error-message {
  color: #f44336;
  font-size: 12px;
  margin-top: 4px;
  line-height: 1.4;
}

/* 密码强度指示器 */
.password-strength {
  display: flex;
  align-items: center;
  margin-top: 6px;
  padding-left: 2px;
}

.strength-label {
  font-size: 13px;
  color: #8892b0;
  margin-right: 8px;
}

.strength-bars {
  flex: 1;
  max-width: 100px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
}

.strength-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease, background-color 0.3s ease;
}

.strength-1 {
  width: 33%;
  background-color: #f44336;
}

.strength-2 {
  width: 66%;
  background-color: #ff9800;
}

.strength-3 {
  width: 100%;
  background-color: #4caf50;
}

.strength-text {
  margin-left: 8px;
  font-size: 12px;
  color: #64ffda;
}

/* 隐私条例同意 */
.privacy-agreement {
  margin: 20px 0;
  padding: 0;
}

.privacy-checkbox {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  user-select: none;
}

.checkbox-input {
  width: 18px;
  height: 18px;
  margin-right: 8px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: var(--accent-cyan);
  flex-shrink: 0;
}

.checkbox-label {
  font-size: 14px;
  color: var(--text-muted);
  line-height: 1.6;
  flex: 1;
}

.privacy-link {
  color: var(--accent-cyan);
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.3s;
}

.privacy-link:hover {
  color: #4dd0e1;
}

/* 隐私政策弹窗 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--card-bg);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border-radius: 20px;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4), 0 0 30px rgba(100, 255, 218, 0.1);
  border: 1px solid rgba(100, 255, 218, 0.1);
  display: flex;
  flex-direction: column;
}

.privacy-modal {
  max-width: 600px;
  max-height: 80vh;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--text-bright);
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  font-size: 24px;
  color: var(--text-muted);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.close-btn:hover {
  background: rgba(100, 255, 218, 0.1);
  color: var(--accent-cyan);
}

.modal-body {
  padding: 0;
  overflow-y: auto;
  flex: 1;
}

.privacy-content {
  max-height: calc(80vh - 140px);
  overflow-y: auto;
  padding: 20px;
  line-height: 1.8;
  color: var(--text-bright);
}

.privacy-content::-webkit-scrollbar {
  width: 6px;
}

.privacy-content::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.privacy-content::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.privacy-section {
  margin-bottom: 24px;
}

.privacy-section h4 {
  color: var(--accent-cyan);
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  margin-top: 0;
}

.privacy-section p {
  color: var(--text-muted);
  font-size: 14px;
  margin-bottom: 10px;
  text-align: justify;
}

.privacy-footer {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
}

.update-date {
  color: var(--text-muted);
  font-size: 12px;
  margin: 0;
}

.modal-footer {
  padding: 16px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.btn-primary {
  background: linear-gradient(135deg, var(--accent-cyan), var(--accent-blue));
  color: var(--bg-deep);
  font-weight: 600;
}

.btn-primary:hover {
  transform: scale(1.02);
  box-shadow: 0 0 20px rgba(100, 255, 218, 0.5);
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.1);
  color: var(--text-bright);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.3);
}

.login-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.login-btn:disabled:hover {
  transform: none;
  box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
}

/* 隐私条例震动效果 */
@keyframes shake {
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-5px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(5px);
  }
}

.privacy-agreement.shake {
  animation: shake 0.5s ease-in-out;
}

/* 弹窗动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-active .modal-content,
.modal-fade-leave-active .modal-content {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-fade-enter-from {
  opacity: 0;
}

.modal-fade-enter-from .modal-content {
  opacity: 0;
  transform: translateY(-20px) scale(0.96);
}

.modal-fade-enter-to {
  opacity: 1;
}

.modal-fade-enter-to .modal-content {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.modal-fade-leave-from {
  opacity: 1;
}

.modal-fade-leave-from .modal-content {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-leave-to .modal-content {
  opacity: 0;
  transform: translateY(-15px) scale(0.95);
}

/* 移动端适配 - 平板和手机 */
@media (max-width: 900px) {
  .register-container {
    height: 100vh;
    height: 100dvh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 10px;
  }
  
  .split-container {
    flex-direction: column;
    transform: skewY(0deg);
    padding: 0;
    width: 100%;
    max-width: 100%;
    min-height: 100%;
    justify-content: flex-start;
    align-items: center;
  }
  
  .left-intro-area {
    transform: skewY(0deg);
    padding-right: 0;
    text-align: center;
    margin-bottom: 30px;
    width: 100%;
    padding: 0 15px;
  }
  
  .right-login-area {
    transform: skewY(0deg);
    width: 100%;
    justify-content: center;
    flex: 1;
    display: flex;
    align-items: flex-start;
    padding: 0 15px;
  }
  
  .register-card {
    width: 100%;
    max-width: 100%;
    padding: 30px 20px 0 20px;
    border-radius: 16px;
    margin-bottom: 20px;
  }
  
  .register-card:hover {
    transform: none;
  }
  
  .hero-title {
    font-size: 2rem;
    line-height: 1.3;
    margin-bottom: 15px;
  }
  
  .hero-desc {
    font-size: 1rem;
    line-height: 1.5;
    max-width: 100%;
  }
  
  .card-header h2 {
    font-size: 1.5rem;
  }
  
  .card-header p {
    font-size: 0.9rem;
  }
  
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .form-col {
    width: 100%;
  }
  
  .form-group {
    margin-bottom: 18px;
  }
  
  .custom-input {
    font-size: 16px;
    padding: 14px 16px;
    height: auto;
    min-height: 48px;
  }
  
  .code-group {
    flex-direction: column;
    gap: 12px;
  }
  
  .code-input {
    width: 100%;
    margin-right: 0;
  }
  
  .btn-code {
    width: 100%;
    padding: 14px;
    font-size: 0.9rem;
    min-height: 48px;
  }
  
  .register-btn {
    width: 100%;
    padding: 16px;
    font-size: 1rem;
    min-height: 50px;
    margin-top: 10px;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 15px;
    padding: 20px 0;
    text-align: center;
  }
  
  .footer-link {
    font-size: 0.85rem;
  }
}

/* 小屏手机适配 */
@media (max-width: 480px) {
  .register-container {
    padding: 5px;
  }
  
  .left-intro-area {
    margin-bottom: 20px;
    padding: 0 10px;
  }
  
  .hero-title {
    font-size: 1.75rem;
    margin-bottom: 12px;
  }
  
  .hero-desc {
    font-size: 0.9rem;
    line-height: 1.4;
  }
  
  .right-login-area {
    padding: 0 10px;
  }
  
  .register-card {
    padding: 25px 15px 0 15px;
    border-radius: 12px;
  }
  
  .card-header h2 {
    font-size: 1.3rem;
    margin-bottom: 8px;
  }
  
  .card-header p {
    font-size: 0.85rem;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  .custom-input {
    padding: 12px 14px;
    font-size: 16px;
    min-height: 44px;
  }
  
  .register-btn {
    padding: 14px;
    font-size: 0.95rem;
    min-height: 48px;
  }
  
  .card-footer {
    padding: 15px 0;
    gap: 12px;
  }
  
  .footer-link {
    font-size: 0.8rem;
  }
}

/* 横屏适配 */
@media (max-width: 900px) and (orientation: landscape) {
  .register-container {
    padding: 10px;
  }
  
  .split-container {
    flex-direction: row;
    align-items: center;
    gap: 20px;
  }
  
  .left-intro-area {
    flex: 0 0 40%;
    margin-bottom: 0;
    padding-right: 20px;
  }
  
  .hero-title {
    font-size: 1.8rem;
  }
  
  .hero-desc {
    font-size: 0.9rem;
  }
  
  .right-login-area {
    flex: 1;
    padding: 0;
  }
  
  .register-card {
    max-width: 600px;
    padding: 25px 25px 0 25px;
  }
}
</style>
